import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { verifyAccount, resetPassword } from "api";
import { Spinner } from "components/common";

type Stage = "verifying" | "set_password" | "done" | "error";

export default function VerifyAccountPage() {
  const { token } = useParams<{ token: string }>();

  const [stage, setStage]           = useState<Stage>("verifying");
  const [resetToken, setResetToken] = useState("");
  const [password, setPassword]     = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [errorMsg, setErrorMsg]     = useState("");

  // Step 1 — verify the token as soon as the page loads
  const verifyMutation = useMutation({
    mutationFn: () => verifyAccount(token!),
    onSuccess: ({ reset_token }) => {
      setResetToken(reset_token);
      setStage("set_password");
    },
    onError: () => {
      setErrorMsg("This verification link is invalid or has expired. Please contact your administrator.");
      setStage("error");
    },
  });

  useEffect(() => {
    if (token) verifyMutation.mutate();
  }, [token]); // eslint-disable-line

  // Step 2 — set the initial password using the reset token returned above
  const passwordMutation = useMutation({
    mutationFn: () => resetPassword(resetToken, password, confirmation),
    onSuccess: () => setStage("done"),
    onError: () => setErrorMsg("Something went wrong. Please try again or contact your administrator."),
  });

  const mismatch = confirmation.length > 0 && password !== confirmation;

  if (stage === "verifying") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-3">
          <Spinner className="h-6 mx-auto" />
          <p className="text-sm text-gray-500">Verifying your account&hellip;</p>
        </div>
      </div>
    );
  }

  if (stage === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="card w-full max-w-sm p-8 text-center space-y-4">
          <h1 className="text-base font-semibold text-gray-900">Verification failed</h1>
          <p className="text-sm text-gray-500">{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (stage === "done") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="card w-full max-w-sm p-8 text-center space-y-4">
          <h1 className="text-base font-semibold text-gray-900">You&rsquo;re all set!</h1>
          <p className="text-sm text-gray-500">Your account is verified and your password has been saved. You can now sign in.</p>
          <a href="/login" className="btn-primary w-full block text-center">Go to sign in</a>
        </div>
      </div>
    );
  }

  // stage === "set_password"
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="card w-full max-w-sm p-8">
        <div className="text-center mb-8">
          <h1 className="text-xl font-semibold text-brand-600">Bridge 13</h1>
          <p className="text-sm text-gray-500 mt-1">Set your password</p>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); passwordMutation.mutate(); }}
          className="space-y-4"
        >
          <div>
            <label className="label">Password</label>
            <input
              type="password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              minLength={8}
              required
              autoFocus
            />
          </div>
          <div>
            <label className="label">Confirm password</label>
            <input
              type="password"
              className={"input " + (mismatch ? "border-red-300" : "")}
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder="Repeat your password"
              required
            />
            {mismatch && <p className="text-xs text-red-500 mt-1">Passwords do not match.</p>}
          </div>
          {errorMsg && <p className="text-sm text-red-500">{errorMsg}</p>}
          <button
            type="submit"
            className="btn-primary w-full"
            disabled={passwordMutation.isPending || mismatch || !password || !confirmation}
          >
            {passwordMutation.isPending ? <Spinner className="h-4" /> : "Set password"}
          </button>
        </form>
      </div>
    </div>
  );
}
